import React, { useEffect, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { Button, Callout, Icon as MeshIcon, Type, Utility } from "mesh-component-library";
import { PlusIcon, PaymentIcon } from "mesh-icon-library";
import { useHistory } from "react-router-dom";
import Form from "../../components/form";
import FormField from "../../components/form-field";
import EpcsIframe from "../../components/epcs";
import PaymentCards from "../../components/svg/PaymentCards/PaymentCards";
import Dropdown from "../../components/dropdown";
import Icon from "../../components/icon";
import FormOption from "../../components/form-option";
import { sendJacadaRequest } from "../../api";
import CONSTANTS, { EPCS_STATUS_SUCCESS, TOW } from "../../app-consts/appConstants";
import {
  doesReduxDriverListHaveBenefitsOperators,
  editPhone,
  getUniqueDriverList,
  isScheduleService,
  nameHandler,
} from "../../utils";
import Spinner from "../../components/spinner";
import FormValidators from "../../utilities/form-validators";
import { generatePath } from "../../utilities/RedirectUtils";
import { useLocale } from "../../contexts/LocaleContext";
import { TRANSLATION_CONSTANTS } from "../../app-consts/translations";
import { UNABLE_TO_COMPLETE_SERVICE } from "../../app-consts/errorMessageConstants";

export type PaymentProps = {
  benefitsEnabled: boolean;
  callId: string;
  commonBenefits: any;
  driverListObj: any;
  epcsInitialize: any;
  epcsStatus: any;
  hasPepSession: any;
  omniStreamlined: boolean,
  partnerDetails: any;
  ppuEnabled: boolean;
  refreshReducersToInitial: () => void;
  requestEpcsInitialize: () => void;
  saveRescueDetailsRetrieveResultsRequest: (payload: any) => void;
  schedulingEnabledInConfiguration: boolean;
  serviceInfo: any;
  serviceRequest: any;
  serviceRequestPayload: any;
  setPreviousTransactionAmount: (payload: string) => void;
  updateCCardInfo: (payload: any) => void;
  updateContactNumber: (payload: string) => void;
  updateDriverList: (payload: any) => void;
  updateEmail: (payload: string) => void;
  updateEpcsStatusIncomplete: () => void;
  updateFirstName: (payload: string) => void;
  updateLastName: (payload: string) => void;
  updateOperatorId: (payload: any) => void;
  updatePromoCode: (payload: string) => void;
  updateServiceTransition: (data: any) => void;
  updateSmsOptIn: (data: any) => void;
  updatedEpcsStatus: () => void;
};

export const Payment = ({
  benefitsEnabled,
  callId,
  commonBenefits,
  driverListObj,
  epcsInitialize,
  epcsStatus,
  hasPepSession,
  omniStreamlined,
  ppuEnabled,
  refreshReducersToInitial,
  requestEpcsInitialize,
  saveRescueDetailsRetrieveResultsRequest,
  serviceInfo,
  serviceRequest,
  serviceRequestPayload,
  setPreviousTransactionAmount,
  updateContactNumber,
  updateCCardInfo,
  updateDriverList,
  updateEmail,
  updateEpcsStatusIncomplete,
  updateFirstName,
  updateLastName,
  updateOperatorId,
  updatePromoCode,
  updateServiceTransition,
  updateSmsOptIn,
  updatedEpcsStatus,
  schedulingEnabledInConfiguration,
  partnerDetails
}: PaymentProps) => {
  const getTranslatedText = useLocale();
  const history = useHistory();

  const schedulingEnabled = isScheduleService(serviceRequestPayload);

  const [showSpinner, setShowSpinner] = useState(false);
  const [name, setName] = useState("");
  const [nameHasError, setNameHasError] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(serviceRequest?.contactNumber);
  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [emailHasError, setEmailHasError] = useState(false);
  const [billingZipCode, setBillingZipCode] = useState("");
  const [cvv, setCvv] = useState("");
  const [cvvHasError, setCvvHasError] = useState(false);
  const [billingZipCodeHasError, setBillingZipCodeHasError] = useState(false);
  const [driverSelectHasError, setDriverSelectHasError] = useState(false);
  const [driverList, setDriverList] = useState([]);
  const [smsOptIn, setSmsOpt] = useState(true);
  const [epcsIframeComponent, setEpcsIframeComponent] = useState<any | null>(null);
  const [selectedDriverPosition, setSelectedDriverPosition] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEpcsReady, setIsEpcsReady] = useState(true);
  const [currentRetry, setCurrentRetry] = useState(0);
  const [isAddContactClicked, setAddContactClicked] = useState(false);

  const userIsPpu = serviceRequest.ppuFlag === "Y" && ppuEnabled;
  const addAMember = commonBenefits?.experience?.addAMember;

  // Set document title, pre-populate fields with existing service request data, and initialize EPCS if necessary on component mount
  useEffect(() => {
    document.title = "Payment | Roadside Assistance";
    setPreExistingDataInState();
    const shouldEpcsInitialize = !shouldRemoveExtraContactInfo && userIsPpu;
    if (shouldEpcsInitialize && !epcsInitialize.success) {
      requestEpcsInitialize();
    }
  }, []);

  // Check EPCS response, attempt retries, and redirect when finished -> error or success
  useEffect(() => {
    redirectOnEpcsResponse();
  }, [epcsStatus]);

  // redirect to Site down on EPCS initialize failure
  useEffect(() => {
    if (epcsInitialize.error) {
      datadogRum.addError(new Error(`Epcs Initialize failure: ${epcsInitialize.error}`));
      history.push(generatePath("sitedown"));
    }
  }, [epcsInitialize]);

  // Check if phone number is valid
  useEffect(() => {
    setIsValidPhoneNumber(/\d{3}-\d{3}-\d{4}/.test(JSON.stringify(phoneNumber)));
  }, [phoneNumber]);

  const setPreExistingDataInState = () => {
    let filteredDriverList: any;
    if (doesReduxDriverListHaveBenefitsOperators(commonBenefits.operators, driverListObj?.driverList)) {
      filteredDriverList = driverListObj?.driverList;
    } else {
      filteredDriverList = getUniqueDriverList({
        drivers: commonBenefits.operators,
        newDriverFirstName: null,
        newDriverLastName: null,
      });
    }
    let name: string;
    if (filteredDriverList?.length === 1) {
      name = filteredDriverList[0].value;
      updateContactName(name);
    } else {
      name = serviceRequest.firstName + " " + serviceRequest.lastName;
    }

    if (filteredDriverList?.length) setDriverList(filteredDriverList);

    setName(name);
    setPhoneNumber(editPhone(serviceRequest.contactNumber));
    setEmailAddress(serviceRequest.email);
    setSmsOpt(serviceRequest.smsOptIn === "Y");
    setBillingZipCode(epcsStatus.request?.billingZip);
  };

  const redirectOnEpcsResponse = () => {
    setShowSpinner(false);
    if (isSubmitting && epcsStatus.success && epcsStatus.response?.maskedCardNumber && !epcsStatus.completed) {
      updatedEpcsStatus();
      datadogRum.addAction("Payment or Contact Information Confirmed");
      history.push(generatePath("location"));
    } else if (epcsStatus.error) {
      datadogRum.addError(new Error(`Epcs failure: ${epcsStatus.error}`));
      history.push(generatePath("sitedown"));
    } else if (epcsStatus?.retry && epcsStatus.retry >= 3) {
      datadogRum.addError(new Error(`Payment Status failure: ${epcsStatus?.response?.paymentStatus}`));
      updateServiceTransition({
        errorMessageContent: UNABLE_TO_COMPLETE_SERVICE,
      });
      history.push(generatePath("ServiceTransition"));
    } else if (epcsStatus?.retry && epcsStatus.retry < 3 && epcsStatus.retry > currentRetry) {
      setCurrentRetry(epcsStatus.retry)
      setIsSubmitting(false);
    }
  };

  const emailOnChange = ({ target: { value, required } }) => {
    setEmailAddress(value);

    if (required || emailHasError) {
      setEmailHasError(!isEmailValid(value));
    }
  };

  const isEmailValid = (emailAddress: string) => {
    const regex = /(\w(=?@)\w+\.{1}[a-zA-Z]{2,})/i;
    return (!userIsPpu && !emailAddress.length) || regex.test(emailAddress);
  };

  const validateEmail = () => {
    setEmailHasError(!isEmailValid(emailAddress));
  };

  const nameOnChange = ({ target: { value } }) => {
    setName(value.trim());

    if (nameHasError) {
      setNameHasError(!isNameValid(value));
    }
  };

  const isNameValid = (name: string) => {
    const regex = /\w+\s{1}\w+/i;
    return regex.test(name);
  };

  const validateName = () => {
    setNameHasError(!isNameValid(name));

    if(isNameValid(name)) {
      updateContactName();
    }
  };

  const phoneOnChange = ({ target: { value } }) => {
    const editedPhoneNumber = editPhone(value);
    setPhoneNumber(editedPhoneNumber);
    return phoneNumber;
  };

  const billingZipCodeOnChange = ({ target: { value } }) => {
    setBillingZipCode(value.slice(0, 5));

    if (billingZipCodeHasError) {
      setBillingZipCodeHasError(!isZipValid(value));
    }
  };

  const cvvOnChange = ({target: {value}}) => {
    setCvv(value.slice(0, 3));
    if (!cvvHasError) {
      setCvvHasError(value.length !== 3);
    }
  };

  const validateCvv = (e) => {
    setCvvHasError(e.target.value.length !== 3);
  };

  const isZipValid = (zip:string ) => {
    const regex = /^\d{5}(-\d{4})?$/;
    return regex.test(zip);
  };

  const validateBillingZipCode = () => {
    setBillingZipCodeHasError(!isZipValid(billingZipCode));
  };

  const handleZipBackspace = (e) => {
    const { keyCode, target } = e;
    let isNotDeleting = keyCode !== CONSTANTS.DELETE && keyCode !== CONSTANTS.BACKSPACE;
    let zipIsTooLong = target.value?.toString().length >= target.maxLength;
    let inputIsNotANumber = ![0, 1, 2, 3, 4, 5, 6, 7, 8, 9].includes(Number(e.key));

    if (isNotDeleting && (zipIsTooLong || inputIsNotANumber)) {
      e.preventDefault();
    }

    if (target.name === "billingZip") {
      setBillingZipCodeHasError(target.value.length > -1 && target.value.length < 4);
    }

    if (target.name === "cvv") {
      setCvvHasError(target.value.length > -1 && target.value.length < 2);
    }
  };

  const selectedDriverOnChange = ({ target }) => {
    setDriverSelectHasError(!target.value);
    setName(target.value);
    setSelectedDriverPosition(target.selectedIndex - 1);
    updateContactName();
  };

  const smsHandler = () => {
    setSmsOpt(!smsOptIn);
  };

  const updateContactName = (singleDriverName?: string) => {
    let firstName: string, lastName: string;

    if (driverList?.length && !isAddContactClicked) {
      firstName = driverList[selectedDriverPosition].firstname;
      lastName = driverList[selectedDriverPosition].lastname;
    } else if (singleDriverName) {
      firstName = nameHandler(singleDriverName).firstName;
      lastName = nameHandler(singleDriverName).lastName;
    } else {
      firstName = nameHandler(name).firstName;
      lastName = nameHandler(name).lastName;
    }

    updateFirstName(firstName);
    updateLastName(lastName);

    if (!!omniStreamlined) {
      saveRescueDetailsRetrieveResultsRequest({
        serviceType: serviceInfo?.service,
        history: history,
        dispatcher: () => refreshReducersToInitial
      });
    }
  }

  const updateUserInfo = () => {
    let operatorId;

    if(driverList?.length && !isAddContactClicked) {
      operatorId = driverList[selectedDriverPosition]?.operatorid
    }

    if (operatorId) {
      updateOperatorId(operatorId);
    }
    updateSmsOptIn(smsOptIn ? "Y" : "N");
    updateEmail(emailAddress);
    updateContactNumber(phoneNumber);
    if (ppuEnabled && serviceRequest.ppuFlag === "Y") {
      updateCCardInfo({ billingZipCode });
    }
  };

  const sendSubmitEventToJacada = (smsOptIn, firstName, lastName, emailAddress, phone) => {
    let eventsData = [];
    eventsData.push(
      { name: "sms_feedbck_consnt_ind", value: smsOptIn },
      { name: "first_name", value: firstName },
      { name: "last_name", value: lastName },
      { name: "smtp_addr", value: emailAddress },
      { name: "phone_nbr", value: phone }
    );

    let jacadaRequestData = {
      externalSystemId: localStorage.getItem("conversationId"),
      events: eventsData,
    };
    sendJacadaRequest(jacadaRequestData);
  };

  const handleEpcsValidationError = () => {
    setShowSpinner(false);
    // The EPCS iframe controller re-initializes after a validation error; block
    // Confirm until it reports ready again to prevent duplicate submissions.
    setIsEpcsReady(false);
  };

  const handleSubmit = () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setShowSpinner(true);
    const { firstName, lastName } = nameHandler(name);
    sendSubmitEventToJacada(smsOptIn, firstName, lastName, emailAddress, phoneNumber);
    updateUserInfo();
    if (isAddContactClicked) {
      const finalDriverList = getUniqueDriverList({
        drivers: driverList,
        newDriverFirstName: firstName,
        newDriverLastName: lastName,
      });
      updateDriverList(finalDriverList);
    }
    if (userIsPpu) {
      if (epcsStatus.response?.maskedCardNumber) {
        updatedEpcsStatus();
        datadogRum.addAction("Payment or Contact Information Confirmed");
        history.push(generatePath("location"));
        return;
      }

      let amount = "0";
      amount = serviceRequest?.CCardInfo?.transactionAmount || "0";
      const previousTransactionAmount = serviceRequest?.previousTransactionAmount || "0";
      const serviceType = serviceInfo?.serviceType || "";
      if (!!epcsIframeComponent) {
        epcsIframeComponent.handleEditCCSubmit(billingZipCode, serviceType, parseFloat(amount), callId, cvv, parseFloat(previousTransactionAmount));
        setPreviousTransactionAmount(amount);
      }
      updateEpcsStatusIncomplete();
    } else {
      updatedEpcsStatus();
      history.push(generatePath("location"));
    }
  };

  const renderCreditCardFormFields = () => {
    if (hasPepSession && serviceInfo?.serviceText === TOW) {
      datadogRum.addError(new Error("[renderCreditCardFormFields] PPU Payment attempted with PEP session for a Tow"), { serviceRequest });
    }
    return (
      <div className="contact-payment-contain">
        <div className="contact-payment-header">
          <h4 className="contact-payment-titles">{getTranslatedText(TRANSLATION_CONSTANTS.PAYMENT_INFO)}</h4>
          <div className="payment-cards-container">
            <PaymentCards />
          </div>
        </div>
        <Callout icon={PaymentIcon}>
          <Type size="xl" align="left" data-testid="payment-compliance-message">
            {getTranslatedText(TRANSLATION_CONSTANTS.PAYMENT_COMPLIANCE_MESSAGING)}
          </Type>
        </Callout>
        <div className="l-grid__col epcsContain">
          {epcsInitialize.success ? (
            <EpcsIframe
              tabIndex="4"
              scrolling="no"
              epcsInitialize={epcsInitialize.initialize}
              onValidationError={handleEpcsValidationError}
              onIframeReadyChange={setIsEpcsReady}
              onRef={(i) => setEpcsIframeComponent(i)}
              history={history}
              setIsSubmitting={(value) => setIsSubmitting(value)}
            />
          ) : null}
          {!!epcsStatus?.response?.paymentStatus && epcsStatus?.response?.paymentStatus !== EPCS_STATUS_SUCCESS && (
            <Utility.Div
              utils={{
                vrBottom: "md",
              }}
            >
              <Type variant="critical" weight="bold">
                {getTranslatedText(TRANSLATION_CONSTANTS.CARD_RETRY_MESSAGE)}
              </Type>
            </Utility.Div>
          )}
          <div className="splitPayment">
            <FormField
              errorText={getTranslatedText(TRANSLATION_CONSTANTS.ENTER_DIGIT_ZIP_CODE)}
              id="billingZip"
              name="billingZip"
              label={getTranslatedText(TRANSLATION_CONSTANTS.BILLING_ZIP)}
              x-autocompletetype="postal-code"
              autoComplete="postal-code"
              type="number"
              maxLength={5}
              value={billingZipCode}
              onKeyPress={(e) => handleZipBackspace(e)}
              onChange={billingZipCodeOnChange}
              onBlur={validateBillingZipCode}
              hasError={billingZipCodeHasError}
              tabIndex="5"
              required
            />
            {omniStreamlined &&
              <FormField
                id="cvv"
                name="cvv"
                label="Security Code"
                type="number"
                maxLength={3}
                value={cvv}
                onKeyPress={(e) => handleZipBackspace(e)}
                onChange={cvvOnChange}
                onBlur={validateCvv}
                hasError={cvvHasError}
                tabIndex="5"
                required
              />
            }
          </div>
        </div>
      </div>
    );
  };

  const renderSubmitButton = () => {
    let buttonIsDisabled: boolean;
    if (userIsPpu && ppuEnabled) {
      buttonIsDisabled =
        isSubmitting ||
        !name ||
        nameHasError ||
        !phoneNumber ||
        !isValidPhoneNumber ||
        !emailAddress ||
        emailHasError ||
        !billingZipCode ||
        billingZipCodeHasError;
      if (!!epcsStatus?.response?.paymentStatus) {
        buttonIsDisabled = epcsStatus.response.paymentStatus !== EPCS_STATUS_SUCCESS;
      }
      buttonIsDisabled = buttonIsDisabled || !isEpcsReady;
      if(omniStreamlined) {
        buttonIsDisabled = buttonIsDisabled || !cvv || cvv.length < 3;
      }
    } else if (benefitsEnabled) {
      buttonIsDisabled =
        isSubmitting ||
        !name ||
        nameHasError ||
        !phoneNumber ||
        !isValidPhoneNumber ||
        emailHasError ||
        (driverSelectHasError && !isAddContactClicked);
    } else if (hasPepSession || (!ppuEnabled && !benefitsEnabled)) {
      buttonIsDisabled = isSubmitting || !name || nameHasError || !phoneNumber || !isValidPhoneNumber || emailHasError;
    }
    return (
      <>
        <div className="payment-meta text_left mt-3">
          <label
            dangerouslySetInnerHTML={{ __html: getTranslatedText(TRANSLATION_CONSTANTS.COMPLIANCE_CONFIRMATION) }}
            data-testid="compliance-message"
          ></label>
        </div>
        <div className="c-modal-dialog__footer--payment hasBenefits u-vr-2-top">
          <Button
            size="lg"
            hasUpgrade
            id="payment-confirm"
            data-testid="ppu-add-payment-button"
            type="submit"
            disabled={buttonIsDisabled}
            onClick={handleSubmit}
            utils={{
              fullWidth: true,
            }}
          >
            {getTranslatedText(TRANSLATION_CONSTANTS.CONFIRM)}
          </Button>
        </div>
      </>
    );
  };
  const shouldRemoveExtraContactInfo = !ppuEnabled && !benefitsEnabled;

  const addContact = () => {
    setAddContactClicked(true);
    setName("");
  };

  return (
    <div role="main">
      {showSpinner && <Spinner />}

      <div className="paymentModalHeader c-modal-dialog__header">
        <button
          onClick={() => history.push(generatePath("location"))}
          className={`c-btn c-btn--icon u-float--right`}
          aria-label="Close Modal"
        >
          <Icon icon="cross" color="brand" size={1} className="icon-rotation" />
        </button>
        <h3 id="payment-header" className="c-modal__title">
          {commonBenefits.operators?.length && !isAddContactClicked
            ? getTranslatedText(TRANSLATION_CONSTANTS.WHICH_DRIVER_NEEDS_HELP)
            : getTranslatedText(TRANSLATION_CONSTANTS.ADD_CONTACT)}
        </h3>
      </div>

      <Form className="contactPayment contactPayment--standalone" validateOnBlur={true}>
        <div className="l-grid">
          <div className="l-grid__col">
            <div className="c-container--contactInfo">
              {commonBenefits.operators?.length && driverList.length && !isAddContactClicked ? (
                <Dropdown
                  id="driver"
                  name="driver"
                  label={getTranslatedText(TRANSLATION_CONSTANTS.DRIVER)}
                  className="selectDriver"
                  boldArrow={true}
                  hasError={driverSelectHasError}
                  errorText={getTranslatedText(TRANSLATION_CONSTANTS.SELECT_A_DRIVER)}
                  value={name}
                  onChange={selectedDriverOnChange}
                  defaultOption={{ value: "", text: getTranslatedText(TRANSLATION_CONSTANTS.SELECT_ONE) }}
                  options={driverList}
                />
              ) : (
                <FormField
                  errorText={getTranslatedText(TRANSLATION_CONSTANTS.ENTER_FIRST_LAST_NAME)}
                  label={getTranslatedText(TRANSLATION_CONSTANTS.NAME)}
                  id="name"
                  className="name"
                  minLength={3}
                  name="name"
                  x-autocompletetype="name-full"
                  autoComplete="name"
                  value={name}
                  onChange={nameOnChange}
                  onBlur={validateName}
                  hasError={nameHasError}
                  tabIndex="1"
                  required
                />
              )}
              <FormField
                errorText={getTranslatedText(TRANSLATION_CONSTANTS.ENTER_VALID_EMAIL_ADDRESS)}
                label={getTranslatedText(TRANSLATION_CONSTANTS.EMAIL)}
                id="email"
                name="email"
                type="email"
                x-autocompletetype="email"
                autoComplete="email"
                value={emailAddress}
                onChange={emailOnChange}
                onBlur={validateEmail}
                hasError={emailHasError}
                tabIndex="2"
                required={userIsPpu || !shouldRemoveExtraContactInfo}
              />

              <FormField
                id="phone"
                errorText={getTranslatedText(TRANSLATION_CONSTANTS.ENTER_PHONE_NUMBER)}
                name="phone"
                label={getTranslatedText(TRANSLATION_CONSTANTS.PHONE_NUMBER)}
                type="tel"
                onChange={phoneOnChange}
                value={phoneNumber}
                validators={[FormValidators.phoneNumberValidator]}
                x-autocompletetype="phone-full"
                autoComplete="tel"
                required
              />
              <div>
                <FormOption
                  data-dd-privacy="allow"
                  onChange={smsHandler}
                  className={`sms-check ${(schedulingEnabled && schedulingEnabledInConfiguration) ? "c-grey_out" : ""}`}
                  id="sms-check"
                  label={getTranslatedText(TRANSLATION_CONSTANTS.SEND_TEXT_MESSAGES)}
                  name="sms-check"
                  checked={smsOptIn}
                  required={true}
                  data-testid="sms-text-checkbox"
                  disabled={(schedulingEnabled && schedulingEnabledInConfiguration)}
                />
              </div>
              {addAMember && commonBenefits.operators?.length > 0 && !isAddContactClicked && (
                <Button
                  size="md"
                  hasUpgrade
                  isHollow
                  shape="square"
                  onClick={addContact}
                  utils={{
                    vrTop: "md",
                  }}
                  data-testid="add-contact-button"
                >
                  <MeshIcon icon={PlusIcon} />{" "}
                  <Type variant="darker" weight="bold">
                    {getTranslatedText(TRANSLATION_CONSTANTS.ADD_CONTACT)}
                  </Type>
                </Button>
              )}
            </div>
          </div>

          {shouldRemoveExtraContactInfo ? null : userIsPpu && renderCreditCardFormFields()}
          {renderSubmitButton()}
        </div>
      </Form>
    </div>
  );
};
